import {useAnalytics} from '@/hooks/useAnalytics';
import type {ProductWidget} from '@/lib/TypesOpenApi';
import type {DeepReadonly} from '@/lib/typings/readonly';
import {useCallback} from 'react';
import {proxy} from 'valtio';
import type {InternetPlan} from '../InternetPlanStore';
import type {ProductBox} from './ProductBox';
import {resetPoints} from './points';

export interface InternetAddons {
  productId: string;
  selected: boolean;
  productWidget?: DeepReadonly<ProductWidget>;
}

export type InternetBox = ProductBox<InternetPlan> & {
  productWidget?: DeepReadonly<ProductWidget>;
  _internetAddons?: InternetAddons[];
  internetAddons?: InternetAddons[];
};

// if add any new field, add the new field to the below reset function also
export const internet = proxy<InternetBox>({
  // No items preselected
  _product: undefined,

  productWidget: undefined,

  _internetAddons: undefined,

  get selected() {
    // internet not have inner rules for it,
    // but will use same interface to decrease copy paste
    return this._product;
  },

  get productId() {
    return this.selected?.value;
  },

  get price() {
    return this.selected?.price || 0;
  },

  get name() {
    return this.selected?.productName || '';
  },

  get internetAddons() {
    return this._internetAddons;
  },
});

export const resetInternet = () => {
  internet._product = undefined;
  internet.productWidget = undefined;
  resetInternetAddons();
};

export const resetInternetAddons = () => {
  internet._internetAddons = undefined;
};

export const useSetInternetPlan = () => {
  const analytics = useAnalytics();

  return useCallback(
    (plan: DeepReadonly<InternetPlan>, previousPlanId?: string) => {
      internet._product = plan as InternetPlan;
      resetPoints();

      if (previousPlanId !== plan.value) {
        analytics.trackInternetPlanCardSelection(
          previousPlanId || '',
          plan.value,
        );
      }
    },
    [analytics],
  );
};

export const setSelectedInternetPlanWidget = (
  widget: DeepReadonly<ProductWidget>,
) => (internet.productWidget = widget);

export const setInternetAddon = (
  productId: string,
  selected: boolean,
  productWidget?: DeepReadonly<ProductWidget>,
) => {
  const newAddon = {
    productId,
    productWidget,
    selected,
  };
  if (!internet._internetAddons) {
    internet._internetAddons = [newAddon];
  } else {
    const addOnIndex = internet?._internetAddons.findIndex(
      addon => addon.productId === productId,
    );
    if (addOnIndex > -1) {
      internet._internetAddons[addOnIndex].selected = selected;
    } else {
      internet._internetAddons.push(newAddon);
    }
  }
};
